// Blocos dos indicadores novos do Comercial, do Marketing e da Diretoria (#6507):
// custo para trazer um cliente, qualidade do lead de mídia, de onde vêm os contratos e
// a rede de parceiros. As contas estão em useComercialFase2.ts.
import React from 'react';
import { rotuloDoMes } from '../../src/lib/dashboard/metasMensais';
import { META_QUALIDADE_DO_LEAD, type LinhaDaRede, type OrigemDoContrato, type ParceiroDaRede } from '../../src/lib/visaoGeral/comercialFase2';
import { lerMes } from '../../src/lib/visaoGeral/periodo';
import { CartaoIndicador, PainelDetalhe, plural } from './componentes';
import { DIAS_CONVERSAO_ORIGEM, useCustoPorCliente, useQualidadeDoLead } from './useComercialFase2';

const reais0 = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const pct1 = (v: number) => `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const kwh = (v: number) => `${Math.round(v).toLocaleString('pt-BR')} kWh`;
const mesAnterior = (mes: string) => { const { ano, mes0 } = lerMes(mes)!; const d = new Date(ano, mes0 - 1, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const carregando = <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />;

// ─────────────────────────── Custo para trazer um cliente ───────────────────────────

export const CartaoCustoPorCliente: React.FC<{ mes: string }> = ({ mes }) => {
  const c = useCustoPorCliente(mes);
  const a = c?.atual;
  return (
    <CartaoIndicador
      titulo="Custo para trazer um cliente"
      valor={c ? (a!.custo == null ? '—' : reais0(a!.custo)) : null}
      detalhe={c ? [
        a!.contratos
          ? `${reais0(a!.gasto)} em mídia ÷ ${plural(a!.contratos, 'contrato aprovado', 'contratos aprovados')} de mídia paga (${kwh(a!.kwh)}).`
          : `${reais0(a!.gasto)} em mídia e nenhum contrato de mídia paga aprovado no mês.`,
        // Sem gasto importado no mês anterior não há com o que comparar.
        c.anterior.custo != null && c.anterior.gasto > 0 ? `${rotuloDoMes(mesAnterior(mes))}: ${reais0(c.anterior.custo)} (${plural(c.anterior.contratos, 'contrato', 'contratos')}).` : null,
      ].filter(Boolean).join(' ') : undefined}
      para="/marketing/campanhas"
      origem="Investimento no Meta Ads no mês ÷ negociações com origem 'Mídia paga' aprovadas na validação de dados no mês (data da aprovação). O contrato aprovado no mês pode ter vindo de anúncio de meses antes."
    />
  );
};

// ─────────────────────────── Qualidade do lead ───────────────────────────

export const CartaoQualidadeDoLead: React.FC<{ mes: string; corrente: boolean }> = ({ mes, corrente }) => {
  const { dados: q } = useQualidadeDoLead(mes);
  const a = q?.atual;
  return (
    <CartaoIndicador
      titulo="Qualidade do lead de mídia"
      valor={q ? (a!.taxa == null ? '—' : pct1(a!.taxa)) : null}
      meta={a?.taxa != null ? { texto: `meta ${pct1(META_QUALIDADE_DO_LEAD)}`, atingido: a.taxa / META_QUALIDADE_DO_LEAD } : undefined}
      detalhe={q ? [
        `${a!.qualificados.toLocaleString('pt-BR')} de ${a!.leads.toLocaleString('pt-BR')} leads de mídia criados no mês chegaram à Qualificação.`,
        q.anterior.taxa != null ? `${rotuloDoMes(mesAnterior(mes))}: ${pct1(q.anterior.taxa)} (${q.anterior.qualificados} de ${q.anterior.leads.toLocaleString('pt-BR')}).` : null,
      ].filter(Boolean).join(' ') : undefined}
      nota={corrente ? 'Mês em curso: os leads dos últimos dias ainda podem chegar à Qualificação.' : undefined}
      para="/crm"
      origem="Leads com origem 'Mídia paga' criados no mês que passaram pela etapa Qualificação, estão dali em diante (inclusive Pós-Venda) ou já tiveram simulação. Desde 26/08/2026 o funil tem a etapa 'Em contato' antes da Qualificação: os meses anteriores não se comparam."
    />
  );
};

// ─────────────────────────── De onde vêm os contratos ───────────────────────────

const conversaoTexto = (o: OrigemDoContrato) => (o.leads
  ? `${o.aprovadosDosLeads} de ${o.leads.toLocaleString('pt-BR')} leads (${pct1(o.conversao!)})`
  : 'sem leads novos');

/** Cartão para a Diretoria: total de contratos do mês e de onde vieram. */
export const CartaoOrigemDosContratos: React.FC<{ linhas: OrigemDoContrato[] | null }> = ({ linhas }) => {
  const comContrato = linhas?.filter(o => o.aprovados > 0) ?? [];
  const total = comContrato.reduce((s, o) => s + o.aprovados, 0);
  const lider = comContrato[0];
  return (
    <CartaoIndicador
      titulo="De onde vêm os contratos"
      valor={linhas ? String(total) : null}
      detalhe={linhas ? (lider ? `contratos aprovados no mês; ${lider.origem} na frente, com ${lider.aprovados}.` : 'Nenhum contrato aprovado no mês.') : undefined}
      composicao={comContrato.map(o => ({
        rotulo: o.origem,
        valor: `${o.aprovados} · ${kwh(o.kwh)}`,
        dica: `Conversão em ${DIAS_CONVERSAO_ORIGEM} dias: ${conversaoTexto(o)}`,
      }))}
      para="/crm/analytics"
      origem="Negociações aprovadas no mês (mesma conta da energia contratada) pela origem do lead no CRM."
    />
  );
};

/** Painel da Gestão Comercial: aprovados no mês e conversão de cada origem. */
export const PainelOrigemDosContratos: React.FC<{ linhas: OrigemDoContrato[] | null; rotuloMes: string }> = ({ linhas, rotuloMes }) => {
  const visiveis = linhas?.filter(o => o.aprovados > 0 || o.leads >= 5) ?? [];
  const maior = Math.max(1, ...visiveis.map(o => o.aprovados));
  return (
    <PainelDetalhe titulo="De onde vêm os contratos" acao={{ rotulo: 'Analytics', para: '/crm/analytics' }}>
      {!linhas ? carregando : visiveis.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum contrato aprovado e nenhum lead novo em {rotuloMes}.</p>
      ) : (
        <ul className="space-y-3">
          {visiveis.map(o => (
            <li key={o.origem}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="min-w-0 truncate text-gray-700 dark:text-gray-200">{o.origem}</span>
                <span className="whitespace-nowrap tabular-nums text-gray-500 dark:text-gray-400">
                  <strong className="text-gray-900 dark:text-white">{o.aprovados}</strong> {o.aprovados === 1 ? 'aprovado' : 'aprovados'}{o.kwh > 0 ? ` · ${kwh(o.kwh)}` : ''}
                </span>
              </div>
              <span className="mt-1 block h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                <span className="block h-full rounded-full bg-primary/70" style={{ width: `${(o.aprovados / maior) * 100}%` }} />
              </span>
              <span className={`mt-1 block text-xs ${o.leads >= 50 && o.aprovadosDosLeads === 0 ? 'text-amber-700 dark:text-amber-400' : 'text-gray-400'}`}>
                Conversão em {DIAS_CONVERSAO_ORIGEM} dias: {conversaoTexto(o)}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-gray-400 mt-3">
        Aprovados em {rotuloMes} pela origem do lead no CRM. Conversão: dos leads criados nos {DIAS_CONVERSAO_ORIGEM} dias até o fim do mês, quantos já estão aprovados (os mais novos ainda podem fechar).
      </p>
    </PainelDetalhe>
  );
};

// ─────────────────────────── Rede de parceiros ───────────────────────────

const LISTA_PARCEIROS = 8;

export const PainelRedeDeParceiros: React.FC<{
  rede: { linhas: LinhaDaRede[]; ativos: number; novos: ParceiroDaRede[]; cadastrados: number } | null;
  rotuloMes: string;
}> = ({ rede, rotuloMes }) => {
  const kwhTotal = rede?.linhas.reduce((s, l) => s + l.kwh, 0) ?? 0;
  const comKwh = rede?.linhas.filter(l => l.kwh > 0).length ?? 0;
  return (
    <PainelDetalhe titulo="Rede de parceiros">
      {!rede ? carregando : (
        <>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {[
              { valor: `${rede.ativos}`, rotulo: `ativos de ${rede.cadastrados} cadastrados` },
              { valor: Math.round(kwhTotal).toLocaleString('pt-BR'), rotulo: comKwh ? `kWh aprovados, ${Math.round(kwhTotal / comKwh).toLocaleString('pt-BR')} por parceiro que fechou` : 'kWh aprovados no mês' },
              { valor: `${rede.novos.length}`, rotulo: rede.novos.length === 1 ? 'parceiro novo' : 'parceiros novos' },
            ].map(c => (
              <div key={c.rotulo} className="rounded-xl bg-gray-50 dark:bg-gray-900/40 p-3 min-w-0">
                <span className="block text-lg font-bold text-gray-900 dark:text-white tabular-nums truncate">{c.valor}</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">{c.rotulo}</span>
              </div>
            ))}
          </div>
          {rede.linhas.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum parceiro trouxe lead ou teve contrato aprovado em {rotuloMes}.</p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {rede.linhas.slice(0, LISTA_PARCEIROS).map(l => (
                <li key={l.parceiro.id} className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-0.5 sm:gap-3 py-2 text-sm">
                  <span className="min-w-0 truncate text-gray-700 dark:text-gray-200">{l.parceiro.nome || 'Sem nome'}</span>
                  <span className="whitespace-nowrap tabular-nums text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                    {plural(l.leads, 'lead', 'leads')} · {plural(l.aprovados, 'aprovado', 'aprovados')}
                    {l.kwh > 0 && <strong className="text-gray-900 dark:text-white"> · {kwh(l.kwh)}</strong>}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-gray-400 mt-3">
            {rede.linhas.length > LISTA_PARCEIROS ? `Mais ${plural(rede.linhas.length - LISTA_PARCEIROS, 'parceiro ativo', 'parceiros ativos')}. ` : ''}
            {rede.novos.length > 0 ? `Novos em ${rotuloMes}: ${rede.novos.map(p => p.nome || 'sem nome').join(', ')}. ` : ''}
            Ativo = trouxe lead ou teve contrato aprovado no mês; conta o que é do parceiro e dos vendedores dele.
          </p>
        </>
      )}
    </PainelDetalhe>
  );
};
